/**
 * The equine SUBTYPE vocabulary (ADR-66 PR 2). ONE canonical definition, used by
 * the equine kind's attribute schema, tenant/sale settings validation, the
 * accepted-subtype resolver, the consign-form projection, and createDraft's
 * server-side validation - so a subtype cannot be smuggled past the browser
 * buttons (which are NOT a policy boundary).
 */
import { z } from 'zod';

/** Every equine subtype the identity model reserves. `zebra`/`zebra_hybrid` are
 *  RESERVED - part of the enum so a value can exist, but NOT offerable on a sale
 *  until the regulated/exotic policy layer exists. */
export const EQUINE_TYPES = ['horse', 'mule', 'donkey', 'hinny', 'zebra', 'zebra_hybrid'] as const;
export type EquineType = (typeof EQUINE_TYPES)[number];

/** The subtypes a SALE may offer and a consignor may pick TODAY - a strict subset
 *  of EQUINE_TYPES with zebra/zebra_hybrid excluded (reserved). */
export const SALE_SELECTABLE_EQUINE_TYPES = ['horse', 'mule', 'donkey', 'hinny'] as const;
export type SaleSelectableEquineType = (typeof SALE_SELECTABLE_EQUINE_TYPES)[number];

/** Platform default when a company/sale configures no equine subtypes: horse only,
 *  so a plain horse sale behaves exactly as before. */
export const DEFAULT_ACCEPTED_EQUINE_TYPES: readonly SaleSelectableEquineType[] = ['horse'];

const SALE_SELECTABLE = new Set<string>(SALE_SELECTABLE_EQUINE_TYPES);

export function isSaleSelectableEquineType(x: unknown): x is SaleSelectableEquineType {
  return typeof x === 'string' && SALE_SELECTABLE.has(x);
}

/**
 * Fail-closed parse of a stored or submitted `acceptedEquineTypes` list: keep only
 * SALE-SELECTABLE subtypes (drops an unknown `'unicorn'` AND a reserved `'zebra'`),
 * dedupe, preserve order. An empty result is "not set here" (null), so the caller
 * falls through to the default rather than to "accept nothing". Never throws.
 * Deliberately NOT `parseAcceptedItemTypes` - that accepts arbitrary strings; a
 * subtype policy needs the finite vocabulary.
 */
export function parseAcceptedEquineTypes(raw: unknown): readonly SaleSelectableEquineType[] | null {
  const parsed = z.array(z.string()).safeParse(raw);
  if (!parsed.success) return null;
  const valid = [...new Set(parsed.data)].filter(isSaleSelectableEquineType);
  return valid.length > 0 ? valid : null;
}

/** The stored subtype VALUE enum (full reserved model), for the equine kind's
 *  attribute schema and the Annie fact - a lot may carry `equineType='zebra'`
 *  even though a sale cannot offer it. */
export const equineTypeSchema = z.enum(EQUINE_TYPES);

/** The write-time validator for a sale/company `acceptedEquineTypes` array - only
 *  sale-selectable values are accepted at the API boundary (reserved zebra and
 *  unknowns are rejected). */
export const saleSelectableEquineTypeSchema = z.enum(SALE_SELECTABLE_EQUINE_TYPES);
